import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, resolveBackendAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import { Check, Filter, Trash2, X } from "lucide-react";

const STATUS_FILTERS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

function formatDateTime(value) {
  return new Date(value).toLocaleString("es-ES");
}

export default function AdminGaleriaVisitantes() {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [dateFilter, setDateFilter] = useState("");
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    api.get("/visitor-gallery/days")
      .then((response) => setDays(Array.isArray(response.data) ? response.data : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) params.date = dateFilter;
      const { data } = await api.get("/admin/visitor-gallery", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (photoId, nextStatus) => {
    setBusyId(photoId);
    try {
      await api.patch(`/admin/visitor-gallery/${photoId}`, { status: nextStatus });
      toast.success("Estado actualizado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const removePhoto = async (photoId) => {
    if (!confirm("¿Eliminar esta foto definitivamente?")) return;
    setBusyId(photoId);
    try {
      await api.delete(`/admin/visitor-gallery/${photoId}`);
      toast.success("Foto eliminada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-galeria-visitantes">
      <header>
        <h1 className="font-display font-black text-3xl">Galería de visitantes</h1>
        <p className="text-muted-foreground">Revisa, aprueba, rechaza o elimina las fotos enviadas por visitantes.</p>
      </header>

      <div className="bg-white rounded-2xl border border-border p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Filter size={14} /> Filtros
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-border px-3 py-2 text-sm"
          data-testid="visitor-gallery-filter-status"
        >
          {STATUS_FILTERS.map((item) => (
            <option key={item.value || "all"} value={item.value}>{item.label}</option>
          ))}
        </select>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="rounded-lg border border-border px-3 py-2 text-sm"
          data-testid="visitor-gallery-filter-date"
        >
          <option value="">Todos los días</option>
          {days.map((day) => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-border p-8 text-muted-foreground">
          Cargando fotos...
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl border border-destructive/30 p-6 text-destructive" data-testid="visitor-gallery-error">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-8 text-center text-muted-foreground" data-testid="visitor-gallery-empty">
          No hay fotos para estos filtros.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => {
            const busy = busyId === item.id;
            const imageUrl = resolveBackendAssetUrl(item.url);
            return (
              <article
                key={item.id}
                className="bg-white rounded-2xl border border-border overflow-hidden"
                data-testid={`visitor-gallery-card-${item.id}`}
              >
                <div className="relative aspect-square bg-accent">
                  {imageUrl ? (
                    <img src={imageUrl} alt={item.comment || item.filename} className="absolute inset-0 h-full w-full object-cover" />
                  ) : null}
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display font-bold">{item.date}</div>
                      <div className="text-xs text-muted-foreground">{formatDateTime(item.created_at)}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${STATUS_STYLES[item.status] || "bg-accent text-foreground"}`}>
                      {item.status}
                    </span>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {item.comment?.trim() || "Sin comentario"}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {item.status !== "approved" ? (
                      <button
                        onClick={() => updateStatus(item.id, "approved")}
                        disabled={busy}
                        className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-2 rounded-full text-sm font-medium disabled:opacity-60"
                        data-testid={`visitor-gallery-approve-${item.id}`}
                      >
                        <Check size={14} /> Aprobar
                      </button>
                    ) : null}

                    {item.status !== "rejected" ? (
                      <button
                        onClick={() => updateStatus(item.id, "rejected")}
                        disabled={busy}
                        className="inline-flex items-center gap-1 bg-foreground text-background px-3 py-2 rounded-full text-sm font-medium disabled:opacity-60"
                        data-testid={`visitor-gallery-reject-${item.id}`}
                      >
                        <X size={14} /> Rechazar
                      </button>
                    ) : null}

                    <button
                      onClick={() => removePhoto(item.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground px-3 py-2 rounded-full text-sm font-medium disabled:opacity-60"
                      data-testid={`visitor-gallery-delete-${item.id}`}
                    >
                      <Trash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
